import type { Company } from "@/services/api/companies";
import { format } from "date-fns";
import { Button } from "@/shared/ui/button";
import { Pencil, Square, SquareX } from "lucide-react";

interface CompaniesTableRowProps {
  company: Company;
  onToggleStatus: (company: Company) => void;
  onEdit: (company: Company) => void;
  isUpdating?: boolean;
}

export function CompaniesTableRow({
  company,
  onToggleStatus,
  onEdit,
  isUpdating = false,
}: CompaniesTableRowProps) {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM d, yyyy HH:mm");
    } catch {
      return "-";
    }
  };

  return (
    <tr className="hover:bg-grid-50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-ink-900">{company.name}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <a
          href={company.linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate max-w-xs block cursor-pointer"
        >
          {company.linkedInUrl}
        </a>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-600">
        {formatDate(company.createdDate)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center gap-2 text-sm text-ink-700">
          <span
            className={`h-2 w-2 rounded-full ${
              company.isActive ? "bg-emerald-500" : "bg-grid-300"
            }`}
          />
          {company.isActive ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(company)}
            disabled={isUpdating}
            className="h-8 w-8"
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleStatus(company)}
            disabled={isUpdating}
            className={`h-8 w-8 ${company.isActive ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" : "text-ink-400 hover:text-ink-500"}`}
          >
            {company.isActive ? (
              <SquareX className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            <span className="sr-only">{company.isActive ? "Deactivate" : "Activate"}</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}
